import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { pathgen } from 'object-path-generator';
import { Box, Button, Column, Divider, FullBox, MenuItem, Row, Select, TextField, Tooltip, Typography } from 'seagull';
import { DiscountModel, serviceCostModel } from '~/models';
import { utils } from '~/utils';
import styles from './BundlePriceEditorContent.module.scss';
export const BundlePriceEditorContentTestIds = pathgen('BundlePriceEditorContentTestIds');
export function BundlePriceEditorContent(props) {
    const { data, service, onDataChange, onCancel, onInputFocus, onInputBlur, showActions = true } = props;
    const { bundle } = service;
    const suffix = serviceCostModel.isVariablePrice(service.cost) ? '/' + serviceCostModel.formattedUnitNameShort(service.cost) : undefined;
    const { bundleRate, discount, discountType, includedServiceRates } = data;
    const discountedPrice = useMemo(()=>{
        if (!discount) return undefined;
        return utils.applyDiscount(bundleRate, DiscountModel.make(discountType, new Decimal(discount))).toNumber();
    }, [
        bundleRate,
        discount,
        discountType
    ]);
    const onIncludedServiceRateChange = (serviceTemplateId)=>(e)=>{
            const newValue = e.currentTarget.value;
            const asNumber = +newValue;
            if (asNumber >= 0) {
                const newIncludedServiceRates = _object_spread_props(_object_spread({}, includedServiceRates), {
                    [serviceTemplateId]: asNumber
                });
                onDataChange(_object_spread_props(_object_spread({}, data), {
                    bundleRate: Object.values(newIncludedServiceRates).reduce((sum, rate)=>sum + rate, 0),
                    includedServiceRates: newIncludedServiceRates
                }));
            }
        };
    const onDiscountChange = (e)=>{
        const newValue = e.currentTarget.value;
        const asNumber = +newValue;
        if (!newValue) {
            onDataChange(_object_spread_props(_object_spread({}, data), {
                discount: ''
            }));
        } else if (asNumber >= 0) {
            onDataChange(_object_spread_props(_object_spread({}, data), {
                discount: asNumber
            }));
        }
    };
    const onDiscountTypeChange = (newDiscountType)=>{
        onDataChange(_object_spread_props(_object_spread({}, data), {
            discountType: newDiscountType
        }));
    };
    const discountError = useMemo(()=>{
        if (discount !== '') {
            if (discountType === 'value') {
                if (bundleRate < discount) {
                    return "Discount value can't exceed the price";
                }
            } else if (discountType === 'percent' && discount > 100) {
                return 'Provide a value up to 100%';
            }
        }
        return '';
    }, [
        discount,
        discountType,
        bundleRate
    ]);
    return /*#__PURE__*/ _jsxs(Column, {
        className: styles.root,
        children: [
            /*#__PURE__*/ _jsxs(Column, {
                className: styles.bundleRate,
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        className: styles.bundleRateRow,
                        children: [
                            /*#__PURE__*/ _jsx(Tooltip, {
                                placement: "top",
                                title: "To adjust the bundle rate, update the rates of the included services",
                                children: /*#__PURE__*/ _jsx(TextField, {
                                    collapseHelperText: false,
                                    isMoney: true,
                                    disabled: true,
                                    testId: BundlePriceEditorContentTestIds.BundleRate(),
                                    label: "Rate",
                                    prefix: "$",
                                    suffix: suffix,
                                    value: bundleRate,
                                    onFocus: onInputFocus,
                                    onBlur: onInputBlur
                                })
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                collapseHelperText: false,
                                isPercentage: discountType === 'percent',
                                isMoney: discountType === 'value',
                                inputProps: {
                                    min: 0
                                },
                                testId: BundlePriceEditorContentTestIds.BundleDiscount(),
                                label: "Discount",
                                value: discount,
                                onChange: onDiscountChange,
                                error: !!discountError,
                                helperText: discountError,
                                suffix: /*#__PURE__*/ _jsx(Box, {
                                    children: /*#__PURE__*/ _jsxs(Select, {
                                        collapseHelperText: false,
                                        value: discountType,
                                        onChange: (e)=>onDiscountTypeChange(e.target.value),
                                        preventOutline: true,
                                        testId: BundlePriceEditorContentTestIds.BundleDiscountType(),
                                        children: [
                                            /*#__PURE__*/ _jsx(MenuItem, {
                                                value: "percent",
                                                children: "%"
                                            }),
                                            /*#__PURE__*/ _jsx(MenuItem, {
                                                disabled: true,
                                                tooltipProps: {
                                                    title: 'Discount can only be applied as a percentage'
                                                },
                                                value: "value",
                                                children: "$"
                                            })
                                        ]
                                    })
                                }),
                                onFocus: onInputFocus,
                                onBlur: onInputBlur
                            })
                        ]
                    }),
                    discountedPrice && /*#__PURE__*/ _jsxs(Typography, {
                        as: "span",
                        testId: BundlePriceEditorContentTestIds.FinalPriceSubtitle(),
                        children: [
                            "Price after discount:",
                            ' ',
                            /*#__PURE__*/ _jsxs(Typography, {
                                as: "span",
                                weight: "normal",
                                testId: BundlePriceEditorContentTestIds.FinalPrice(),
                                children: [
                                    "$",
                                    utils.formatNumberWithCommas(discountedPrice)
                                ]
                            }),
                            suffix && /*#__PURE__*/ _jsxs(Typography, {
                                as: "span",
                                children: [
                                    "\xa0",
                                    suffix
                                ]
                            })
                        ]
                    })
                ]
            }),
            (bundle === null || bundle === void 0 ? void 0 : bundle.services) && bundle.services.length > 0 && /*#__PURE__*/ _jsxs(Column, {
                className: styles.includedServices,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        weight: "normal",
                        children: "Included services:"
                    }),
                    bundle.services.map((includedService)=>{
                        const rate = includedServiceRates[includedService.serviceTemplateId || ''] || 0;
                        const hasError = !rate && rate !== 0;
                        return /*#__PURE__*/ _jsxs(Row, {
                            className: styles.includedService,
                            testId: BundlePriceEditorContentTestIds.IncludedService(includedService.serviceTemplateId || ''),
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    style: {
                                        flex: 1
                                    },
                                    children: includedService.name
                                }),
                                /*#__PURE__*/ _jsx(TextField, {
                                    collapseHelperText: false,
                                    testId: BundlePriceEditorContentTestIds.IncludedServiceRate(includedService.serviceTemplateId || ''),
                                    isMoney: true,
                                    inputProps: {
                                        min: 0
                                    },
                                    label: "Rate",
                                    prefix: "$",
                                    width: 120,
                                    value: rate,
                                    onChange: onIncludedServiceRateChange(includedService.serviceTemplateId || ''),
                                    onFocus: onInputFocus,
                                    onBlur: onInputBlur,
                                    error: hasError,
                                    helperText: hasError ? 'Rate is required' : undefined
                                })
                            ]
                        }, includedService.serviceTemplateId);
                    })
                ]
            }),
            showActions && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Divider, {
                        margin: 0
                    }),
                    /*#__PURE__*/ _jsxs(FullBox, {
                        centered: true,
                        flexDirection: "row",
                        className: styles.actions,
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                type: "button",
                                skin: "primaryOutline",
                                onClick: onCancel,
                                testId: BundlePriceEditorContentTestIds.CancelButton(),
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                type: "submit",
                                testId: BundlePriceEditorContentTestIds.SaveButton(),
                                children: "Save"
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
